import React, { useState, useEffect } from 'react';
import { Col, Container, Row, Button, Spinner } from 'reactstrap';
import { Link, useParams } from 'react-router-dom';
import MetaTags from "react-meta-tags";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ApiClient from '../BaseUrl/ApiClient';
import '../LimitedTimeDeals/LTDealDetail.css';

function HHDetail() {
    const { dealId } = useParams();
    const [deal, setDeal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activePhoto, setActivePhoto] = useState(null);

    const fetchDealDetails = async () => {
        setLoading(true);
        setError(null);
        try {
            const client = ApiClient();
            const response = await client.get(`business/deal/happyHours/${dealId}`);
            const fetchedDeal = response.data.responseData.deal;
            setDeal(fetchedDeal);
            if (fetchedDeal?.dealPhoto && fetchedDeal.dealPhoto.length > 0) {
                setActivePhoto(fetchedDeal.dealPhoto[0].url);
            }
        } catch (err) {
            setError('Failed to fetch happy hour details. Please try again.');
            toast.error('Failed to fetch happy hour details.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (dealId) {
            fetchDealDetails();
        }
    }, [dealId]);

    const formatTime = (timeString) => {
        if (!timeString) return 'N/A';

        try {
            // Handle ISO time strings (like "1970-01-01T01:32:00.000Z")
            if (typeof timeString === 'string' && timeString.includes('T')) {
                const date = new Date(timeString);
                if (isNaN(date.getTime())) return 'Invalid time';

                const hours = date.getUTCHours();
                const minutes = date.getUTCMinutes();
                const ampm = hours >= 12 ? 'PM' : 'AM';
                const displayHours = hours % 12 || 12;
                const formattedMinutes = minutes.toString().padStart(2, '0');

                return `${displayHours}:${formattedMinutes} ${ampm}`;
            }

            // Handle simple HH:mm format strings
            if (/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(timeString)) {
                const [hours, minutes] = timeString.split(':');
                const hourNum = parseInt(hours, 10);
                const ampm = hourNum >= 12 ? 'PM' : 'AM';
                const displayHour = hourNum % 12 || 12;
                return `${displayHour}:${minutes} ${ampm}`;
            }

            return 'Invalid time format';
        } catch (e) {
            console.error('Error formatting time:', e);
            return 'Invalid time';
        }
    };

    if (loading) {
        return (
            <div className="page-content">
                <Container fluid className="mt-5">
                    <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '320px' }}>
                        <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                        <h5 className="mt-3 text-muted">Loading Happy Hour Details...</h5>
                    </div>
                </Container>
            </div>
        );
    }

    if (error || !deal) {
        return (
            <div className="page-content">
                <Container fluid className="mt-5">
                    <div className="text-center">
                        <lord-icon
                            src="https://cdn.lordicon.com/msoeawqm.json"
                            trigger="loop"
                            colors="secondary:#121331,secondary:#08a88a"
                            style={{ width: '75px', height: '75px' }}
                        ></lord-icon>
                        <h5 className="mt-2">Sorry! No Happy Hour Found</h5>
                        <p className="text-muted mb-0">{error || 'No happy hour data available.'}</p>
                    </div>
                </Container>
            </div>
        );
    }

    const photos = deal.dealPhoto || [];
    const timeRange = deal.timeRange
        ? `${formatTime(deal.timeRange.startTime)} - ${formatTime(deal.timeRange.endTime)}`
        : 'N/A';

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid className="mt-5">
                    <MetaTags>
                        <title>Happy Hour Detail</title>
                    </MetaTags>
                    <ToastContainer />

                    <div className="mb-3">
                        <Link to="/happy-hour-deal-list">
                            <Button color="primary" className="ltd-back-btn">
                                <i className="mdi mdi-arrow-left"></i> Back to Deals
                            </Button>
                        </Link>
                    </div>

                    <div className="ltd-detail-card">
                        <Row className="g-0">
                            <Col lg="5" md="5">
                                <div className="ltd-image-wrap">
                                    <span className={`ltd-active-badge ${deal.isActive ? 'active' : 'inactive'}`}>
                                        <span className="dot"></span>
                                        {deal.isActive ? 'Available' : 'Unavailable'}
                                    </span>
                                    {photos.length > 0 ? (
                                        <img
                                            src={activePhoto || photos[0].url}
                                            alt={deal.name || 'Happy hour'}
                                            className="ltd-image"
                                        />
                                    ) : (
                                        <span className="ltd-image-placeholder">No image available</span>
                                    )}

                                    {photos.length > 1 && (
                                        <div className="ltd-thumbs">
                                            {photos.map((photo, index) => (
                                                <img
                                                    key={index}
                                                    src={photo.url}
                                                    alt={`Photo ${index + 1}`}
                                                    className="ltd-thumb"
                                                    onClick={() => setActivePhoto(photo.url)}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </Col>

                            <Col lg="7" md="7">
                                <div className="ltd-body">
                                    <span className="ltd-eyebrow">
                                        <i className="mdi mdi-glass-cocktail"></i> Happy Hours
                                    </span>
                                    <h1 className="ltd-title">{deal.name || 'Happy Hour Deal'}</h1>

                                    <div className="ltd-info-grid">
                                        <div className="ltd-info-tile">
                                            <div className="ltd-info-icon">
                                                <i className="mdi mdi-tag-outline"></i>
                                            </div>
                                            <div className="ltd-info-meta">
                                                <span className="ltd-info-label">Category</span>
                                                <span className="ltd-info-value">
                                                    {deal.category?.categoryLogo?.url && (
                                                        <img
                                                            src={deal.category.categoryLogo.url}
                                                            alt={deal.category?.name}
                                                            className="ltd-cat-logo"
                                                        />
                                                    )}
                                                    {deal.category?.name || 'N/A'}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="ltd-info-tile">
                                            <div className="ltd-info-icon">
                                                <i className="mdi mdi-clock-outline"></i>
                                            </div>
                                            <div className="ltd-info-meta">
                                                <span className="ltd-info-label">Happy Hour Time</span>
                                                <span className="ltd-info-value">{timeRange}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="ltd-section-title">Available Days</div>
                                    <div className="ltd-pills">
                                        {deal.days && deal.days.length > 0 ? (
                                            deal.days.map((day, index) => (
                                                <span key={index} className="ltd-day-pill">{day}</span>
                                            ))
                                        ) : (
                                            <span className="ltd-empty">No days specified</span>
                                        )}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Container>
            </div>
        </React.Fragment>
    );
}

export default HHDetail;
